import React, { Component } from 'react';
import { pickBy, isNil, isEmpty, debounce } from 'lodash';
import PropTypes from 'prop-types';
import { Button, Input, Icon, Grid, Select, Checkbox } from 'semantic-ui-react';
import Draggable from 'react-draggable';

import { makeOptionsWithNull, normalizeNumeric } from 'Common/FormHelper';

import { RecipeItemList } from './RecipeItemList';
import {
  DEFAULT_PAGE_SIZE,
  MEAL_COMPONENT_OPTIONS,
  EQUALITY_SIGNS,
  WEEKLY_NUTRIENTS,
  REMAINING_LIST_ITEMS_THRESHOLD,
} from './constants';

import 'Styles/components/recipeSearch.less';

const FORMATTED_WEEKLY_NUTRIENT = WEEKLY_NUTRIENTS.map(x => {
  return {
    ...x,
    Description: x.Description === 'Saturated Fat' ? 'Sat. Fat' : x.Description,
  };
});

class RecipeSearch extends Component {
  static propTypes = {
    headerTitle: PropTypes.string,
    showItemArrows: PropTypes.bool,
    onInputMouseDown: PropTypes.func,
    onSearchCloseClick: PropTypes.func,
    recipeCategories: PropTypes.array,
    recipeSources: PropTypes.array,
    recipeGroups: PropTypes.array,
    searchRecipes: PropTypes.func,
    searchResults: PropTypes.array,
    hasNextPage: PropTypes.bool,
    loadingRecipes: PropTypes.bool,
    droppableId: PropTypes.string,
    onAddRecipe: PropTypes.func,
    onUnmount: PropTypes.func,
    rootContainerProps: PropTypes.object,
    onDisableHoverOver: PropTypes.func,
    disableHoverOver: PropTypes.bool,
    onSearchItemsOnMenuChange: PropTypes.func,
    searchItemsOnMenus: PropTypes.bool,
    enableDnD: PropTypes.bool,
  };

  static defaultProps = {
    headerTitle: 'Recipe Search',
    showItemArrows: true,
    onInputMouseDown: () => {},
    onSearchCloseClick: () => {},
    recipeCategories: [],
    recipeSources: [],
    recipeGroups: [],
    searchRecipes: () => {},
    searchResults: [],
    hasNextPage: false,
    loadingRecipes: false,
    onUnmount: () => {},
    rootContainerProps: {},
    onDisableHoverOver: () => {},
    disableHoverOver: false,
    onSearchItemsOnMenuChange: () => {},
    searchItemsOnMenus: false,
    enableDnD: true,
  };

  PANELS = {
    LEFT: 0,
    RIGHT: 1,
    CENTER: 2,
  };

  PANEL_WIDTHS = {
    [this.PANELS.LEFT]: 320, // width + marginRight
    [this.PANELS.RIGHT]: 360,
    [this.PANELS.CENTER]: 400,
  };

  loadedPagesMap = {};
  recipeItemListRef = React.createRef();
  inputSearchRef = React.createRef();
  page = 1;
  searching = false;

  state = {
    newSearch: true,
    searchText: '',
    categoryOption: null,
    sourceOption: null,
    groupOption: null,
    mealComponentOption: null,
    weeklyNutrientOption: null,
    weeklyNutrientSign: null,
    weeklyNutrientText: '',
    searchItemsOnMenus: this.props.searchItemsOnMenus,
    disableHoverOver: this.props.disableHoverOver,
    page: 1,
    panels: {
      // panel open state
      [this.PANELS.LEFT]: false,
      [this.PANELS.RIGHT]: false,
      [this.PANELS.CENTER]: true,
    },
    // recipe search position
    position: {
      x: 0,
      y: 0,
    },
    items: [],
  };

  componentWillUnmount() {
    const { onUnmount } = this.props;

    onUnmount();
  }

  componentDidMount() {
    this.setState({ position: this.getCenterPosition() });
    if (this.inputSearchRef.current) {
      this.inputSearchRef.current.focus();
    }
  }

  componentDidUpdate(prevProps) {
    const { searchResults: prevResults } = prevProps;
    const { searchResults } = this.props;

    if (prevResults !== searchResults) {
      // Forks the search results from props to allow
      // clearing the item list when waiting on a new query
      this.setState({ items: searchResults });
    }
  }

  searchRecipes = async () => {
    const { searchRecipes } = this.props;
    const {
      newSearch,
      searchText,
      categoryOption,
      sourceOption,
      groupOption,
      mealComponentOption,
      searchItemsOnMenus,
      weeklyNutrientOption,
      weeklyNutrientSign,
      weeklyNutrientText,
    } = this.state;

    const searchingWeeklyNutrient =
      !isNil(weeklyNutrientOption) &&
      !isEmpty(weeklyNutrientText) &&
      !isNil(weeklyNutrientSign);

    const searchParams = pickBy(
      {
        newSearch,
        Search: searchText,
        RecipeCategoryId: categoryOption,
        SourceId: sourceOption,
        GroupId: groupOption,
        Component: mealComponentOption,
        SearchItemsOnMenus: searchItemsOnMenus,
        Page: this.page,
        NumberPerPage: DEFAULT_PAGE_SIZE,
        UsePaging: true,
        ...(searchingWeeklyNutrient && {
          WeeklyNutrient: weeklyNutrientOption,
          WeeklyNutrientMin:
            weeklyNutrientSign === EQUALITY_SIGNS[1].Id
              ? Number(weeklyNutrientText)
              : null,
          WeeklyNutrientMax:
            weeklyNutrientSign === EQUALITY_SIGNS[0].Id
              ? Number(weeklyNutrientText)
              : null,
        }),
      },
      value => !isNil(value)
    );

    if (newSearch) {
      this.loadedPagesMap = {};

      const { current } = this.recipeItemListRef;
      if (current) {
        current.clearListCache();
      }
      this.setState({ items: [], newSearch: false });
    }

    await searchRecipes(searchParams);

    this.loadedPagesMap[this.page] = true;
  };

  debouncedSearchRecipes = debounce(this.searchRecipes, 250);

  resetSearchFilters = () => {
    this.setState({
      categoryOption: null,
      sourceOption: null,
      groupOption: null,
      mealComponentOption: null,
      weeklyNutrientOption: null,
      weeklyNutrientSign: null,
      weeklyNutrientText: '',
    });
  };

  onSearchTextChange = e => {
    const {
      target: { value },
    } = e;

    this.page = 1;
    this.setState({ searchText: value, newSearch: true }, () => {
      this.debouncedSearchRecipes.cancel();
      this.debouncedSearchRecipes();
    });
  };

  onCategoryChange = (e, data) => {
    const { value } = data;

    if (value !== this.state.categoryOption) {
      this.page = 1;
      this.setState({ categoryOption: value, newSearch: true });
    }
  };

  onSourceChange = (e, data) => {
    const { sourceOption } = this.state;
    const { value } = data;

    if (value !== sourceOption) {
      this.setState({ sourceOption: value, newSearch: true });
    }
  };

  onGroupChange = (e, data) => {
    const { groupOption } = this.state;
    const { value } = data;

    if (value !== groupOption) {
      this.setState({ groupOption: value, newSearch: true });
    }
  };

  onSearchOnMenusChange = () => {
    this.page = 1;
    const nextValue = !this.state.searchItemsOnMenus;
    this.setState(
      { searchItemsOnMenus: nextValue, newSearch: true },
      async () => {
        await this.searchRecipes();

        if (this.recipeItemListRef.current) {
          this.recipeItemListRef.current.clearListCache();
        }

        this.props.onSearchItemsOnMenuChange(nextValue);
      }
    );
  };

  onMealComponentChange = (e, data) => {
    const { mealComponentOption } = this.state;
    const { value } = data;

    if (value !== mealComponentOption) {
      this.setState({ mealComponentOption: value, newSearch: true });
    }
  };

  onWeeklyNutrientChange = (e, data) => {
    const { weeklyNutrientOption } = this.state;
    const { value } = data;

    if (value !== weeklyNutrientOption) {
      this.setState({ weeklyNutrientOption: value, newSearch: true });
    }
  };

  onWeeklyNutrientSignChange = (e, data) => {
    const { weeklyNutrientSign } = this.state;
    const { value } = data;

    if (value !== weeklyNutrientSign) {
      this.setState({ weeklyNutrientSign: value, newSearch: true });
    }
  };

  onWeeklyNutrientTextChange = e => {
    const {
      target: { value },
    } = e;

    this.setState({
      newSearch: true,
      weeklyNutrientText: normalizeNumeric(value),
    });
  };

  onDisableHoverOverChange = () => {
    const nextValue = !this.state.disableHoverOver;
    this.setState({ disableHoverOver: nextValue }, () => {
      this.props.onDisableHoverOver(nextValue);
    });
  };

  loadMoreRecipes = ({ startIndex, stopIndex }) => {
    const { hasNextPage } = this.props;

    // Get the page the item index would fall on, pages start from 1
    const page = Math.floor(startIndex / DEFAULT_PAGE_SIZE) + 1;
    const nextPage = page + 1;
    const nextPageLoaded = this.loadedPagesMap[nextPage];
    const remainingItems = page * DEFAULT_PAGE_SIZE - startIndex;

    // Load the next page of results if getting close to the end list
    if (
      hasNextPage &&
      remainingItems < REMAINING_LIST_ITEMS_THRESHOLD &&
      !nextPageLoaded
    ) {
      this.page = nextPage;
      return new Promise(resolve => {
        this.setState(
          {
            newSearch: false,
          },
          () => {
            resolve(this.searchRecipes());
          }
        );
      });
    }

    // Nothing to request so just resolve
    return Promise.resolve();
  };

  onRecipeSearchIconsMouseDown = e => {
    // Prevents dragging when clicking on the close icon
    e.stopPropagation();
  };

  togglePanel = (panel, opened) => {
    const next = isNil(opened) ? !this.state.panels[panel] : opened;

    if (next === this.state.panels[panel]) {
      return;
    }

    let x = this.state.position.x;
    let openedPanelWidth = 0;

    if (panel === this.PANELS.LEFT) {
      x += (next ? -1 : 1) * this.PANEL_WIDTHS[panel];
    } else if (panel === this.PANELS.RIGHT) {
      Object.entries(this.state.panels).forEach(([p, opened]) => {
        if (Number(p) === panel) {
          openedPanelWidth += (next ? 1 : 0) * this.PANEL_WIDTHS[panel];
        } else if (opened) {
          openedPanelWidth += this.PANEL_WIDTHS[p];
        }
      });
    }

    const position = {
      x,
      y: this.state.position.y,
    };

    if (position.x < 0 && next) {
      position.x = 0;
    }

    if (position.x + openedPanelWidth > window.innerWidth) {
      position.x = window.innerWidth - openedPanelWidth;
    }

    this.setState({
      panels: {
        ...this.state.panels,
        [panel]: next,
      },
      position,
    });
  };

  openLeftPanel = () => {
    this.togglePanel(this.PANELS.LEFT, true);
  };

  openRightPanel = () => {
    this.togglePanel(this.PANELS.RIGHT, true);
  };

  MainPanel = () => {
    const {
      headerTitle,
      showItemArrows,
      onSearchCloseClick,
      onInputMouseDown,
      hasNextPage,
      loadingRecipes,
      droppableId,
      onAddRecipe,
      enableDnD,
    } = this.props;

    const {
      searchText,
      searchItemsOnMenus,
      disableHoverOver,
      items,
    } = this.state;

    return (
      <div className="rs-container">
        <Grid padded>
          <Grid.Row className="rs-header-row">
            <Grid.Column>
              <div className="rs-header">
                <Icon
                  className="rs-filter"
                  name="filter"
                  size="large"
                  link
                  onMouseDown={this.onRecipeSearchIconsMouseDown}
                  onClick={() => this.togglePanel(this.PANELS.LEFT)}
                />
                {headerTitle}
                <Icon
                  className="rs-close"
                  name="close"
                  color="red"
                  size="large"
                  onMouseDown={this.onRecipeSearchIconsMouseDown}
                  onClick={onSearchCloseClick}
                />
              </div>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row className="rs-row">
            <Grid.Column>
              <Input
                placeholder="Smartest Lunchroom Search"
                fluid
                icon={<Icon name="search" />}
                iconPosition="left"
                onChange={this.onSearchTextChange}
                onMouseDown={onInputMouseDown}
                value={searchText}
                loading={loadingRecipes}
                ref={this.inputSearchRef}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row className="rs-row checkboxes">
            <Checkbox
              label="Search items on menus"
              className="rs-checkbox rs-search-checkbox"
              checked={searchItemsOnMenus}
              onChange={this.onSearchOnMenusChange}
            />
            <Checkbox
              label="Show Hover Over"
              className="rs-checkbox"
              checked={!disableHoverOver}
              onChange={this.onDisableHoverOverChange}
            />
          </Grid.Row>
        </Grid>
        <RecipeItemList
          items={items}
          recipeItemProps={{
            showIcon: showItemArrows,
            iconName: 'arrow right',
            iconClass: 'rs-menu-item-arrow',
            bordered: true,
            onAddRecipe,
            disableHover: disableHoverOver,
          }}
          hasNextPage={hasNextPage}
          loadNextPage={this.loadMoreRecipes}
          isNextPageLoading={loadingRecipes}
          droppableId={droppableId}
          ref={this.recipeItemListRef}
          enableDnD={enableDnD}
        />
      </div>
    );
  };

  LeftPanel = () => {
    const { recipeCategories, recipeSources, recipeGroups } = this.props;
    const {
      categoryOption,
      groupOption,
      sourceOption,
      mealComponentOption,
      weeklyNutrientOption,
      weeklyNutrientText,
      weeklyNutrientSign,
    } = this.state;
    return (
      <div className="rs-panel panel-left">
        <Grid padded>
          <Grid.Row className="rs-header-row">
            <Grid.Column>
              <div className="rs-header">
                <div
                  className="rs-filter"
                  onMouseDown={this.onRecipeSearchIconsMouseDown}
                  onClick={this.resetSearchFilters}
                >
                  Reset
                </div>
                <span className="left-panel-header">Filter Recipe Search</span>
                <Icon
                  className="rs-close"
                  name="close"
                  color="red"
                  size="large"
                  onMouseDown={this.onRecipeSearchIconsMouseDown}
                  onClick={() => this.togglePanel(this.PANELS.LEFT)}
                />
              </div>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row className="rs-dropdowns">
            <Grid.Column className="panel rs-dropdown-col">
              <Select
                placeholder="Meal Category"
                options={makeOptionsWithNull(recipeCategories)}
                fluid
                onChange={this.onCategoryChange}
                value={categoryOption}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row className="rs-dropdowns">
            <Grid.Column className="panel rs-dropdown-col">
              <Select
                placeholder="Source"
                options={makeOptionsWithNull(recipeSources)}
                fluid
                onChange={this.onSourceChange}
                value={sourceOption}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row className="rs-dropdowns">
            <Grid.Column className="panel rs-dropdown-col">
              <Select
                placeholder="Group"
                options={makeOptionsWithNull(recipeGroups)}
                fluid
                onChange={this.onGroupChange}
                value={groupOption}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column className="panel rs-dropdown-col">
              <Select
                placeholder="Meal Component"
                options={MEAL_COMPONENT_OPTIONS}
                fluid
                onChange={this.onMealComponentChange}
                value={mealComponentOption}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row className="rs-dropdowns">
            <Grid.Column width="6" className="panel rs-dropdown-col">
              <Select
                options={makeOptionsWithNull(FORMATTED_WEEKLY_NUTRIENT)}
                fluid
                onChange={this.onWeeklyNutrientChange}
                value={weeklyNutrientOption}
              />
            </Grid.Column>
            <Grid.Column width="3" className="panel rs-dropdown-col">
              <Select
                className="equality-dropdown"
                options={makeOptionsWithNull(EQUALITY_SIGNS)}
                fluid
                onChange={this.onWeeklyNutrientSignChange}
                value={weeklyNutrientSign}
              />
            </Grid.Column>
            <Grid.Column width="6" className="panel rs-dropdown-col">
              <Input
                fluid
                onChange={this.onWeeklyNutrientTextChange}
                value={weeklyNutrientText}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <div className="panel-footer">
          <Grid padded>
            <Grid.Row>
              <Grid.Column className="rs-dropdown-col">
                <Button
                  fluid
                  className="filter-submit-btn"
                  onClick={() => {
                    this.page = 1;
                    this.setState(
                      {
                        newSearch: true,
                      },
                      () => {
                        this.togglePanel(this.PANELS.LEFT, false);
                        this.debouncedSearchRecipes.cancel();
                        this.debouncedSearchRecipes();
                      }
                    );
                  }}
                >
                  Show Results
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </div>
    );
  };

  RightPanel = () => <div className="rs-panel panel-right" />;

  handleDrag = (e, { x, y }) => {
    this.setState({ position: { x, y } });
  };

  getCenterPosition = () => {
    const x = Math.max(
      (document.documentElement.clientWidth - 350) / 2,
      (window.innerWidth - 350) / 2 || 0
    );
    const innerHeight = Math.max(
      document.documentElement.clientHeight,
      window.innerHeight || 0
    );
    const y = innerHeight > 768 ? (innerHeight - 850) / 2 : 0;
    return { x, y };
  };

  render() {
    const { rootContainerProps } = this.props;
    const { panels, position } = this.state;

    const { LeftPanel, RightPanel, MainPanel } = this;

    return (
      <Draggable
        handle=".rs-header-row"
        bounds="parent"
        position={position}
        onDrag={this.handleDrag}
      >
        <div className="rs" {...rootContainerProps}>
          {panels[this.PANELS.LEFT] && <LeftPanel />}
          {panels[this.PANELS.CENTER] && <MainPanel />}
          {panels[this.PANELS.RIGHT] && <RightPanel />}
        </div>
      </Draggable>
    );
  }
}

export { RecipeSearch };



// WEBPACK FOOTER //
// ./src/common/Components/RecipeSearch/RecipeSearch.js